'use client';

import { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { usePathname } from 'core/i18n/navigation';
import VisitorHeader from 'modules/VisitorHeader';
import Header from 'modules/Header';
import ActivityRail from 'templates/Dashboard/ActivityRail';
import Footer from 'modules/Footer';
import BoxSidebar from 'elements/Sidebar/BoxSidebar';
import SettingsSidebar from 'elements/Sidebar/SettingsSidebar';
import { useSidebar } from 'xstate/hooks/useSidebar';
import { closeSidebar } from 'xstate/actors/SidebarActor';
import { SidebarView } from 'xstate/machines/SidebarMachine';
import { BackgroundColor, BACKGROUND_COLOR_VALUE } from 'utils/ColorUtils';
import { appRoutes, isSpacesPath } from 'config/routing';
import { useSyncAccountLocale } from 'hooks/useLocale';
import { applyStoredTheme } from 'hooks/useTheme';
import {
  applyMarketingTheme,
  getMarketingTheme,
  useMarketingTheme,
} from 'hooks/useMarketingTheme';
import useAuthenticatedUser from 'networking/mindfeed/hooks/useAuthenticatedUser';
import { useProStatus } from 'networking/revenueCat/hooks/useProStatus';

type Chrome = {
  bg: BackgroundColor;
  transparent?: boolean;
  footerHidden?: boolean;
  /**
   * Page follows the visitor's marketing theme (light ↔ dark marketing look).
   * Only the homepage for now; other marketing pages pin themselves dark
   * until they get their light treatment.
   */
  themed?: boolean;
};

// Public (visitor) chrome config, keyed by the locale-agnostic path (next-intl's
// usePathname strips the locale prefix). Replaces the per-page
// `NextPageWithLayout` statics + Default layout's getLayout.
function getPublicChrome(pathname: string): Chrome {
  if (pathname === '/login') {
    return {
      bg: BackgroundColor.NavyBlue,
      transparent: true,
      footerHidden: true,
      themed: true,
    };
  }
  // All marketing pages follow the light/dark marketing theme. Should a page
  // ever need to opt out again (e.g. a redesign that must stay dark while in
  // progress), move it to its own branch and drop `themed: true` — its header
  // keeps the static dark chrome and the theme toggle disappears (that's how
  // pages were staged while being converted one by one).
  if (
    [
      '/',
      '/about-us',
      '/product/mindfeed-classic',
      '/product/use-cases/memory-keeping',
      '/pricing/individual',
      '/pricing/business',
      '/download',
    ].includes(pathname)
  ) {
    return { bg: BackgroundColor.NavyBlue, transparent: true, themed: true };
  }
  if (pathname === '/help-centre') return { bg: BackgroundColor.White };
  if (pathname === '/blog' || pathname.startsWith('/blog/category')) {
    return { bg: BackgroundColor.SupportiveGray };
  }
  return { bg: BackgroundColor.White };
}

// The authenticated app sections (/spaces, /settings) — chrome is chosen by
// pathname (deterministic across server/client, so no hydration mismatch); the
// authed Header itself is mount-gated because it renders user-specific data
// that isn't available during SSR.
const isSettingsRoute = (p: string) => p.startsWith(appRoutes.SETTINGS.DEFAULT);
const isNotificationsRoute = (p: string) =>
  p.startsWith(appRoutes.NOTIFICATIONS);

export function SiteChrome({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { authenticatedUser } = useAuthenticatedUser();
  const { isPro } = useProStatus();
  const { isOpen, view } = useSidebar();

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isAppRoute =
    isSpacesPath(pathname) ||
    isSettingsRoute(pathname) ||
    isNotificationsRoute(pathname);

  // Apply the persisted colour theme on app routes only. Public (marketing)
  // pages use the marketing theme (`marketing-dark`) instead — keeping the app's
  // `dark` class off <html> there so elements/Button's `dark:` (dashboard)
  // styles don't override the brand-styled marketing buttons.
  useEffect(() => {
    if (isAppRoute) return applyStoredTheme();
    document.documentElement.classList.remove('dark');
  }, [isAppRoute]);

  // Marketing (visitor) theme. The pre-paint script in layout.tsx already set
  // the class; reconcile here in case it was blocked (e.g. CSP, bfcache).
  const { resolvedTheme: marketingTheme } = useMarketingTheme();
  useEffect(() => applyMarketingTheme(getMarketingTheme()), []);

  const publicChrome = isAppRoute ? null : getPublicChrome(pathname);
  const bg = isAppRoute ? BackgroundColor.SupportiveGray : publicChrome!.bg;

  // A signed-in user sees the app in the language set on their account. The
  // server can't look the account up, so it reads the language from a
  // cookie. This keeps that cookie matching the account (after signing in,
  // or after the language is changed) and reloads the page if it changed.
  useSyncAccountLocale(isAppRoute);

  // Paint the document canvas (<html>) so the overscroll / rubber-band region
  // (desktop and mobile Safari) matches the page background instead of showing
  // white — the wrapper <div> below only covers the in-flow area. Themed
  // (marketing-theme-aware) pages follow the resolved marketing theme instead of
  // their static bg.
  const themed = !isAppRoute && publicChrome!.themed;
  useEffect(() => {
    document.documentElement.style.backgroundColor = themed
      ? marketingTheme === 'dark'
        ? BACKGROUND_COLOR_VALUE[BackgroundColor.NavyBlue]
        : BACKGROUND_COLOR_VALUE[BackgroundColor.White]
      : isAppRoute
        ? // The token flips with the app's light/dark theme.
          'var(--color-surface-canvas)'
        : BACKGROUND_COLOR_VALUE[bg];
  }, [bg, themed, marketingTheme, isAppRoute]);

  if (isAppRoute) {
    const variant = isSpacesPath(pathname)
      ? 'dashboard'
      : isSettingsRoute(pathname)
        ? 'settings'
        : 'default';
    return (
      <div className={styles.clip}>
        <div
          className={classNames(
            styles.app,
            'bg-surface-canvas',
            isOpen ? 'ml-[min(100%,420px)]' : 'ml-0',
          )}
        >
          {/* The bar, logo and search are in the server HTML. The user and
              Pro status live in localStorage, so they're only handed over
              after mount to keep the first client render equal to the HTML. */}
          <Header
            user={mounted ? authenticatedUser : undefined}
            isProAccount={mounted && isPro}
            variant={variant}
          />
          {/* One rail for every app page, rendered here rather than by each
              screen, so it stays on screen while a navigation waits for the
              next page (dashboard, search, notifications, settings). */}
          <ActivityRail />
          <main>{children}</main>
          {isOpen && (
            <button
              type="button"
              aria-label="Close navigation"
              onClick={closeSidebar}
              className={styles.scrim}
            />
          )}
        </div>

        <aside
          aria-hidden={!isOpen}
          className={classNames(
            styles.drawer,
            isOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          {view === SidebarView.Settings && <SettingsSidebar />}
          {view === SidebarView.Box && <BoxSidebar />}
        </aside>
      </div>
    );
  }

  const { transparent, footerHidden } = publicChrome!;
  return (
    <div
      className={classNames(
        'flex flex-col min-h-screen justify-between',
        // Themed pages: white with the light theme's dot grid, navy when the
        // marketing theme resolves dark (sections go transparent over it).
        themed ? styles.themedBg : bg.valueOf(),
      )}
    >
      <VisitorHeader
        appearance={transparent ? 'transparent' : 'solid'}
        scrolledAppearance="frosted"
        themed={themed}
      />
      <main>{children}</main>
      {!footerHidden && <Footer />}
    </div>
  );
}

const styles = {
  clip: 'relative overflow-x-clip',
  themedBg: classNames(
    'bg-white bg-[radial-gradient(circle,rgba(39,87,186,0.08)_1px,transparent_1.4px)] bg-[length:28px_28px]',
    'marketing-dark:bg-mf-navy marketing-dark:bg-none',
  ),
  // No justify-between: the page is a plain flex child, so spare vertical space
  // has to stay below it rather than being pushed between it and the header.
  app: classNames(
    'flex flex-col min-h-screen w-screen',
    'transition-[margin] duration-100 ease-in-out',
  ),
  // z-10 keeps it under the bottom tab bar (z-20), and the padding clears it.
  drawer: classNames(
    'lg:hidden fixed top-0 bottom-0 left-0 z-10 w-full max-w-[420px]',
    'overflow-y-auto bg-surface-panel border-r border-hairline',
    'pb-[calc(4rem+env(safe-area-inset-bottom))]',
    'transition-transform duration-300 ease-in-out',
  ),
  scrim: 'lg:hidden absolute inset-0 z-10 bg-black/30',
};
